import { ChatInterface } from './interfaces';

/** Image hosts allowed when splitting history text into image + text messages. */
export const ALLOWED_CHAT_HISTORY_IMAGE_PREFIXES = [
    'https://gloaistagingstorage.blob.core.windows.net/user-images/',
    'https://gloqoaiproductionstorage.blob.core.windows.net/user-images/',
    'https://mario-luigi.s3.ap-northeast-1.amazonaws.com/member-uploaded-files/',
    'https://s3.ap-northeast-1.amazonaws.com/res.bimariobot.com/member-uploaded-files/',
] as const;

/** Status text stripped from history; image is shown without this caption. */
const EXCLUDED_TEXT_SNIPPETS = [
    'Done (upload successful)',
    '(Conversation is closing, do not send message.)',
];

const allowedImageUrlRegex = new RegExp(
    `(?:${ALLOWED_CHAT_HISTORY_IMAGE_PREFIXES.map((prefix) =>
        prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    ).join('|')})[^\\s<>"']+`,
    'gi'
);

const getMessageContent = (item: ChatInterface | Record<string, unknown>): string => {
    const message = (item as ChatInterface).message;
    if (!message) {
        return '';
    }
    if (typeof message === 'string') {
        return message;
    }
    const content = message.content ?? (message as { message?: string }).message;
    return typeof content === 'string' ? content : '';
};

const cleanRemainingText = (raw: string): string => {
    let text = raw;
    for (const snippet of EXCLUDED_TEXT_SNIPPETS) {
        text = text.split(snippet).join('');
    }
    return text
        .replace(/\s*-\s*/g, ' ')
        .replace(/[\n\r]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const extractAllowedImageUrls = (content: string): string[] => {
    const matches = content.match(allowedImageUrlRegex);
    if (!matches) {
        return [];
    }
    const seen = new Set<string>();
    const urls: string[] = [];
    for (const url of matches) {
        const normalized = url.replace(/[.,;:!?)]+$/, '');
        if (!seen.has(normalized)) {
            seen.add(normalized);
            urls.push(normalized);
        }
    }
    return urls;
};

const toImageChat = (item: ChatInterface, url: string): ChatInterface => ({
    ...item,
    type: 'image',
    message: {
        url,
        content: '',
    },
});

const toTextChat = (item: ChatInterface, content: string): ChatInterface => {
    const { url: _url, ...messageWithoutUrl } = item.message ?? { content: '' };
    return {
        ...item,
        type: 'text',
        message: {
            ...messageWithoutUrl,
            content,
        },
    };
};

/**
 * Splits a history text message that embeds allowed image URLs into
 * image message(s) first, then optional text (upload status text excluded).
 */
export const splitChatHistoryTextMessage = (item: ChatInterface): ChatInterface[] => {
    const content = getMessageContent(item);
    if (!content) {
        return [item];
    }

    const imageUrls = extractAllowedImageUrls(content);
    if (imageUrls.length === 0) {
        return [item];
    }

    // Remove allowed image URLs from the string (same regex as extraction) so text
    // never keeps "text_msg - " + link.
    const remaining = content.replace(allowedImageUrlRegex, ' ');
    const textContent = cleanRemainingText(remaining);

    const result: ChatInterface[] = imageUrls.map((url) => toImageChat(item, url));
    if (textContent) {
        result.push(toTextChat(item, textContent));
    }
    return result;
};

/**
 * Normalizes API chat history rows: trim owner, split text+image payloads.
 */
export const normalizeChatHistoryItems = (
    items: ChatInterface[]
): ChatInterface[] => {
    const normalized: ChatInterface[] = [];

    for (const rawItem of items) {
        const item: ChatInterface = {
            ...rawItem,
            owner: rawItem.owner ? (rawItem.owner.trim() as ChatInterface['owner']) : rawItem.owner,
        };

        if (item.type && item.type !== 'text') {
            normalized.push(item);
            continue;
        }

        normalized.push(...splitChatHistoryTextMessage(item));
    }

    return normalized;
};